import type { Gap } from "./gaps";
import type { EdgeSide } from "./level";
import type { MapFile, MapPiece } from "./mapfile";
import type { LedgeSide } from "./prims/box";

// Mirror mode (#119's cheat): a map flipped left to right, x to -x. Done to
// the checked map file before `buildLevel`, so the sim, the reach bot, the
// markers and the meshes all see the same mirrored park.
//
// A piece's `rot` (degrees) points its u axis along (cos, sin); mirrored, that
// is 180 - rot. Every primitive is symmetric across its width (w) except a
// box's ledges, whose w sides swap. Rails and zones just flip their x.

/** An angle in degrees, folded into (-180, 180]. */
function fold(deg: number): number {
  const d = ((deg % 360) + 360) % 360;
  return d > 180 ? d - 360 : d;
}

const flipRot = (rot: number | undefined): number => fold(180 - (rot ?? 0));
/** A heading (0 faces +z, 90 faces +x) mirrored. */
const flipYaw = (yaw: number): number => fold(-yaw);
const flipX = <T extends { x: number }>(p: T): T => ({ ...p, x: p.x === 0 ? 0 : -p.x });

const LEDGE_MIRROR: Record<LedgeSide, LedgeSide> = { "u+": "u+", "u-": "u-", "w+": "w-", "w-": "w+" };
const SIDE_MIRROR: Record<EdgeSide, EdgeSide> = { north: "north", south: "south", east: "west", west: "east" };

function mirrorPiece(p: MapPiece): MapPiece {
  if (p.kind === "rail") return { ...p, a: flipX(p.a), b: flipX(p.b) };
  // A chute (#163) is a path of world points.
  if (p.kind === "chute") return { ...p, path: p.path.map(flipX) };
  // Scenery's path (#156) is offsets along the world's axes from (x, z), so they flip too.
  if (p.kind === "scenery") return { ...flipX(p), rot: flipRot(p.rot), ...(p.path && { path: p.path.map(flipX) }) };
  const out = { ...flipX(p), rot: flipRot(p.rot) };
  if (out.kind === "box" && out.ledges) out.ledges = out.ledges.map((s) => LEDGE_MIRROR[s]);
  // A pool's steps (#163) sit across it on w, which a mirror turns round.
  if (out.kind === "pool" && out.steps) {
    const { rail, ...steps } = out.steps;
    out.steps = { ...steps, w: -steps.w, ...(rail && { rail: rail === "w+" ? "w-" : "w+" }) };
  }
  // So does an L pool's arm (#169).
  if (out.kind === "pool" && out.arm) out.arm = { ...out.arm, side: out.arm.side === "w+" ? "w-" : "w+" };
  return out;
}

function mirrorGap(g: Gap): Gap {
  return { ...g, from: flipX(g.from), to: flipX(g.to) };
}

/** `file` flipped left to right. It still passes `parseMapFile` and `checkPlayable` wherever the original does. */
export function mirrorMapFile(file: MapFile): MapFile {
  const [south, east, north, west] = file.look.walls;
  const goals = file.goals && {
    ...file.goals,
    ...(file.goals.letters && { letters: file.goals.letters.map(flipX) }),
    ...(file.goals.combo && { combo: file.goals.combo.map(flipX) }),
    ...(file.goals.tape && { tape: flipX(file.goals.tape) }),
  };
  return {
    ...file,
    spawn: { ...flipX(file.spawn), yaw: flipYaw(file.spawn.yaw) },
    look: {
      ...file.look,
      walls: [south!, west!, north!, east!],
      // A street end (#236) on the north or south side runs along x, so it flips end for end; east and west swap.
      ...(file.look.streetEnds && {
        streetEnds: file.look.streetEnds.map((e) =>
          e.side === "north" || e.side === "south" ? { ...e, from: -e.to, to: -e.from } : { ...e, side: SIDE_MIRROR[e.side] },
        ),
      }),
      // A shop row (#240) the same way, its shops (listed from `from` to `to`) in the other order along x.
      ...(file.look.shopRows && {
        shopRows: file.look.shopRows.map((r) =>
          r.side === "north" || r.side === "south" ? { ...r, from: -r.to, to: -r.from, shops: [...r.shops].reverse() } : { ...r, side: SIDE_MIRROR[r.side] },
        ),
      }),
    },
    pieces: file.pieces.map(mirrorPiece),
    ...(file.gaps && { gaps: file.gaps.map(mirrorGap) }),
    ...(goals && { goals }),
    ...(file.hubs && { hubs: file.hubs.map((h) => ({ ...flipX(h), yaw: flipYaw(h.yaw) })) }),
    ...(file.edges && { edges: file.edges.map((e) => ({ ...e, side: SIDE_MIRROR[e.side] })) }),
    ...(file.vehicles && { vehicles: file.vehicles.map((v) => ({ ...flipX(v), rot: flipRot(v.rot) })) }),
    ...(file.targets && { targets: file.targets.map((t) => ({ ...flipX(t), yaw: flipYaw(t.yaw) })) }),
    ...(file.graffiti && { graffiti: file.graffiti.map(flipX) }),
    ...(file.props && { props: file.props.map(flipX) }),
  };
}
